import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel, MESES, diasDaSemana } from '@/shared/utils/datas';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { ReadHolidayResponseDto } from '@/api.types';
import { readHoliday } from './holiday.service';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useEffect, useState, useMemo } from 'react';

type Visualizacao = 'calendario' | 'lista';

export default function HolidaysIndexPage() {
  const { setActions } = useBreadcrumbActions();
  const [feriados, setFeriados] = useState<ReadHolidayResponseDto[]>([]);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('calendario');
  const [modalLocaisAberto, setModalLocaisAberto] = useState(false);
  const [feriadoSelecionado, setFeriadoSelecionado] = useState<ReadHolidayResponseDto | null>(null);
  const tableState = useDataTableState('inicio');

  const abrirModalFeriado = (feriado: ReadHolidayResponseDto) => {
    setFeriadoSelecionado(feriado);
    setModalLocaisAberto(true);
  };

  const handleBuscar = () => {
    readHoliday({
      skip: 0,
      take: 1000,
      comDefensorias: true,
      comNucleos: true,
      comSetores: true
    } as any).then((result) => {
      setFeriados(result[0]);
    }).catch((res) => {
      toast.error(String(res));
    });
  };

  useEffect(() => {
    handleBuscar();
  }, []);

  useEffect(() => {
    setActions({ primary: { label: 'Cadastrar', href: Links.Calendario_Criar, onClick: () => { } } });
    return () => setActions({});
  }, [setActions]);

  const getLocaisVigencia = (feriado: ReadHolidayResponseDto): string[] => {
    if (feriado.geral) {
      return ['Todos os locais'];
    }
    const nomesDef = feriado.defensorias?.map((d) => d.nome) || [];
    const nomesNuc = feriado.nucleos?.map((n) => n.nome) || [];
    const nomesSet = (feriado as any).setores?.map((s: any) => s.nome) || [];
    return nomesDef.concat(nomesNuc).concat(nomesSet);
  };

  const formatarPeriodo = (feriado: ReadHolidayResponseDto): string => {
    const di = DataAmigavel(feriado.inicio);
    const df = feriado.fim ? DataAmigavel(feriado.fim) : null;
    return df && di !== df ? `${di} até ${df}` : di;
  };

  // Filtrar feriados por ano
  const feriadosFiltradosPorAno = useMemo(() => {
    if (visualizacao === 'calendario') {
      return feriados;
    }
    return feriados.filter((feriado) => {
      const dataInicio = new Date(feriado.inicio);
      const dataFim = feriado.fim ? new Date(feriado.fim) : null;
      const anoInicio = dataInicio.getFullYear();
      const anoFim = dataFim ? dataFim.getFullYear() : anoInicio;

      // Inclui se o feriado começa ou termina no ano selecionado, ou se o ano está no intervalo
      return anoInicio === ano || anoFim === ano || (anoInicio <= ano && anoFim >= ano);
    });
  }, [feriados, ano, visualizacao]);

  // Colunas para o DataTable
  const colunas: Column<ReadHolidayResponseDto>[] = [
    {
      key: "inicio",
      header: "Período",
      render: (row) => formatarPeriodo(row)
    },
    { key: "nome", header: "Título" },
    {
      key: "locais",
      header: "Locais de Vigência",
      render: (row) => {
        const rd = row.defensorias?.length ?? 0;
        const rc = row.nucleos?.length ?? 0;
        const rs = (row as any).setores?.length ?? 0;
        const t = row.geral ? 0 : rd + rc + rs;

        return (
          <>
            {t && t > 0
              ? (
                <button className="btn btn-link p-0" onClick={() => abrirModalFeriado(row)}>
                  Abrir ({t})
                </button>
              )
              : row.geral ? '(todos)' : ('Nenhuma defensoria/núcleo/setor especificado')
            }
          </>
        );
      }
    },
    {
      key: "acoes",
      header: "Ações",
      render: (row) =>
        <Link className="link-secondary" to={Links.Calendario_Editar + '/' + row.id}>
          Editar
        </Link>
    },
  ];

  // Visualização de Calendário
  const getDiasNoMes = (mes: number, ano: number) => {
    return new Date(ano, mes + 1, 0).getDate();
  };

  const getPrimeiroDiaSemana = (mes: number, ano: number) => {
    return new Date(ano, mes, 1).getDay();
  };

  const getFeriadosNaData = (data: Date): ReadHolidayResponseDto[] => {
    return feriados.filter((feriado) => {
      const dataInicio = new Date(feriado.inicio);
      const dataFim = feriado.fim ? new Date(feriado.fim) : null;

      if (
        dataInicio.getFullYear() === data.getFullYear() &&
        dataInicio.getMonth() === data.getMonth() &&
        dataInicio.getDate() === data.getDate()
      ) {
        return true;
      }

      if (dataFim) {
        const dataSelecionadaTime = new Date(
          data.getFullYear(),
          data.getMonth(),
          data.getDate()
        ).getTime();
        const inicioTime = new Date(
          dataInicio.getFullYear(),
          dataInicio.getMonth(),
          dataInicio.getDate()
        ).getTime();
        const fimTime = new Date(
          dataFim.getFullYear(),
          dataFim.getMonth(),
          dataFim.getDate()
        ).getTime();

        return dataSelecionadaTime >= inicioTime && dataSelecionadaTime <= fimTime;
      }

      return false;
    });
  };

  const hoje = useMemo(() => {
    const h = new Date();
    return new Date(h.getFullYear(), h.getMonth(), h.getDate()).getTime();
  }, []);

  const renderMes = (mes: number) => {
    const diasNoMes = getDiasNoMes(mes, ano);
    const primeiroDiaSemana = getPrimeiroDiaSemana(mes, ano);
    const dias: (number | null)[] = [];

    for (let i = 0; i < primeiroDiaSemana; i++) {
      dias.push(null);
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      dias.push(dia);
    }

    return (
      <div key={mes} className="col-md-3 mb-4">
        <div className="card h-100">
          <div className="card-header text-center bg-light">
            <strong>{MESES[mes]} {ano}</strong>
          </div>
          <div className="card-body p-2">
            <div className="calendario-mes">
              <div className="calendario-dias-semana">
                {diasDaSemana.map((dia, index) => (
                  <div key={index} className="calendario-dia-semana">
                    {dia}
                  </div>
                ))}
              </div>
              <div className="calendario-dias">
                {dias.map((dia, index) => {
                  if (dia === null) {
                    return <div key={index} className="calendario-dia vazio"></div>;
                  }

                  const data = new Date(ano, mes, dia);
                  const dataTime = new Date(
                    data.getFullYear(),
                    data.getMonth(),
                    data.getDate()
                  ).getTime();
                  const isHoje = dataTime === hoje;
                  const feriadosNaData = getFeriadosNaData(data);
                  const temFeriado = feriadosNaData.length > 0;

                  return (
                    <div
                      key={index}
                      className={`calendario-dia ${isHoje ? 'hoje' : ''} ${temFeriado ? 'com-evento' : ''}`}
                      onClick={() => {
                        if (temFeriado) {
                          abrirModalFeriado(feriadosNaData[0]);
                        }
                      }}
                      style={{
                        cursor: temFeriado ? 'pointer' : 'default'
                      }}
                      title={temFeriado ? feriadosNaData.map(f => f.nome).join(', ') : ''}
                    >
                      {dia}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleAnoChange = (value: string) => {
    const numValue = Number(value);
    if (numValue >= 2000 && numValue <= 9999) {
      setAno(numValue);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3 className="pt-4">Bloqueios</h3>
        </div>
      </div>
      <hr />

      <div className="row mb-3">
        <div className="col-md-12">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="ano-input" className="mb-0">Ano:</label>
              <input
                id="ano-input"
                type="number"
                className="form-control"
                style={{ width: '100px' }}
                min="2000"
                max="9999"
                value={ano}
                onChange={(e) => handleAnoChange(e.target.value)}
              />
            </div>
            <div className="btn-group ms-auto" role="group">
              <button
                type="button"
                className={`btn ${visualizacao === 'calendario' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('calendario')}
              >
                Calendário
              </button>
              <button
                type="button"
                className={`btn ${visualizacao === 'lista' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setVisualizacao('lista')}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {visualizacao === 'calendario' ? (
        <div className="row">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(renderMes)}
        </div>
      ) : (
        feriadosFiltradosPorAno && (
          <DataTable
            columns={colunas}
            data={feriadosFiltradosPorAno}
            initialSortKey="inicio"
            onChange={handleBuscar}
            serverSide={false}
            states={tableState}
            totalRecords={feriadosFiltradosPorAno.length}
            showFilter={false}
            renderFilters={() => <> </>}
          />
        )
      )}

      <Modal show={modalLocaisAberto} onHide={() => setModalLocaisAberto(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {feriadoSelecionado?.nome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feriadoSelecionado && (
            <>
              <div className="mb-3">
                <strong>Período:</strong> {formatarPeriodo(feriadoSelecionado)}
              </div>
              <div className="mb-3">
                <strong>Locais de Vigência:</strong>
                <ul className="mt-2">
                  {getLocaisVigencia(feriadoSelecionado).map((nome, index) => (
                    <li key={index}>{nome}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {feriadoSelecionado && (
            <Link
              className="btn btn-primary"
              to={Links.Calendario_Editar + '/' + feriadoSelecionado.id}
            >
              Editar
            </Link>
          )}
          <button className="btn btn-secondary" onClick={() => setModalLocaisAberto(false)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      {visualizacao === 'calendario' && (
        <style>{`
          .calendario-mes {
            display: flex;
            flex-direction: column;
          }
          
          .calendario-dias-semana {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 2px;
            margin-bottom: 4px;
          }
          
          .calendario-dia-semana {
            text-align: center;
            font-weight: bold;
            font-size: 0.75rem;
            padding: 4px 2px;
            color: #666;
          }
          
          .calendario-dias {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 2px;
          }
          
          .calendario-dia {
            aspect-ratio: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.85rem;
            border: 1px solid #e0e0e0;
            border-radius: 3px;
            transition: all 0.2s;
            min-height: 30px;
          }
          
          .calendario-dia.vazio {
            border: none;
            background: transparent;
          }
          
          .calendario-dia:hover:not(.vazio) {
            background-color: #f0f0f0;
          }
          
          .calendario-dia.hoje {
            background-color: #ffff76;
            font-weight: bold;
          }
          
          .calendario-dia.com-evento {
            background-color: #28a745 !important;
            color: white !important;
            font-weight: bold;
          }
          
          .calendario-dia.com-evento:hover {
            background-color: #218838 !important;
          }
        `}</style>
      )}
    </>
  );
}
